import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  // Reconcile presentation only: never synthesize an add or change cart contents.
  #refreshPromise;
  #refreshController;
  #refreshGeneration = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    window.addEventListener('pageshow', this.#handlePageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    window.removeEventListener('pageshow', this.#handlePageShow);
    this.#invalidateRefresh();
  }

  #handleCartAdd = () => {
    // A real update owns newer state than any pending read.
    this.#invalidateRefresh();
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #invalidateRefresh = () => {
    this.#refreshGeneration++;
    this.#refreshController?.abort();
    this.#refreshController = undefined;
    this.#refreshPromise = undefined;
  };

  #handlePageShow = (event) => {
    const navigation = performance.getEntriesByType('navigation')[0];
    if (event.persisted || navigation?.type === 'back_forward') {
      void this.#refreshCart();
    }
  };

  #refreshCart = () => {
    if (this.#refreshPromise) return this.#refreshPromise;
    const generation = ++this.#refreshGeneration;
    const controller = new AbortController();
    this.#refreshController = controller;
    const root = window.Shopify?.routes?.root || '/';
    const components = Array.from(document.querySelectorAll('cart-items-component'));
    const sectionIds = [...new Set(components.map((item) => item.dataset.sectionId).filter(Boolean))];
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.set('sections', sectionIds.join(','));
    const options = { cache: 'no-store', credentials: 'same-origin', signal: controller.signal };

    const pending = (async () => {
      try {
        if (!sectionIds.length || sectionIds.length > 5) throw new Error('Cart sections unavailable');
        const responses = await Promise.all([
          fetch(root + 'cart.js', { ...options, headers: { Accept: 'application/json' } }),
          fetch(url, options),
        ]);
        if (responses.some((response) => !response.ok)) throw new Error('Cart refresh failed');
        const [cart, sections] = await Promise.all(responses.map((response) => response.json()));
        if (!Number.isInteger(cart.item_count) || cart.item_count < 0) throw new Error('Invalid cart count');
        // Validate all fragments before updating any visible component.
        const parsed = new Map(sectionIds.map((id) => {
          if (typeof sections[id] !== 'string') throw new Error('Cart section unavailable');
          return [id, new DOMParser().parseFromString(sections[id], 'text/html')];
        }));
        const replacements = components.map((current) => {
          const fresh = Array.from(parsed.get(current.dataset.sectionId).querySelectorAll('cart-items-component'))
            .find((item) => item.dataset.sectionId === current.dataset.sectionId);
          if (!fresh) throw new Error('Cart component unavailable');
          return [current, fresh];
        });
        if (generation !== this.#refreshGeneration || controller.signal.aborted || !this.isConnected) return false;
        for (const [current, fresh] of replacements) {
          morph(current, fresh);
          current.updatedCallback?.();
        }
        this.querySelector('dialog')?.classList.toggle('cart-drawer--empty', cart.item_count === 0);
        for (const icon of document.querySelectorAll('cart-icon')) {
          icon.renderCartBubble?.(cart.item_count, false, false);
        }
        return true;
      } catch (error) {
        if (controller.signal.aborted || generation !== this.#refreshGeneration) return false;
        console.warn('Cart presentation could not be refreshed');
        return 'unavailable';
      } finally {
        if (this.#refreshController === controller) {
          this.#refreshController = undefined;
          this.#refreshPromise = undefined;
        }
      }
    })();
    this.#refreshPromise = pending;
    return pending;
  };

  async open() {
    const refreshed = await this.#refreshCart();
    if (!this.isConnected) return;
    if (refreshed === 'unavailable') {
      // Keep the ordinary server-rendered cart available if Ajax is unavailable.
      window.location.assign((window.Shopify?.routes?.root || '/') + 'cart');
      return;
    }
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
